import { useState } from 'react';
import { Check, Package, X } from 'lucide-react';
import {
  availableAssetsFor,
  calcAssetBookingPrice,
  findFreeAssetSlots,
  assetBookingEndTime,
} from '../../../engine';
import { PayForBooking, paymentDueFor } from '../../booking/PayForBooking';
import { relativeDayLabel } from '../memberData';
import { Card, EmptyState, SectionTitle } from '../mobileUi';
import { useT, type MessageKey } from '../../../i18n';

// ─────────────────────────────────────────────────────────────────────
// EQUIPMENT ON ITS OWN, IN THE MEMBER APP (#911 slice 2, ASSET_BOOKING.md § 6b).
//
// The «Utstyr» mode of the booking screen: a ball machine for an hour, with
// no court — held and paid for on its own, through `POST /member/asset-bookings`
// and `/member/asset-bookings/:id/payment`. Equipment WITH a court is the
// fieldset inside the court sheet in `MobileBook.tsx` (§ 6a); this file is the
// other half.
//
// It reads the same engine the server validates and prices with —
// `findFreeAssetSlots` / `availableAssetsFor` for what is free,
// `calcAssetBookingPrice` for what it costs, on the STORED configuration and
// the machine's own zones — so the number a member is shown is the number the
// server charges, and a machine offered here is one the server will book
// unless somebody takes it first, which the server answers with a code.
//
// The owner's rule is one payment, always to the centre (SETTLEMENT.md § 1–2);
// nothing here knows or cares who owns the machine.
// ─────────────────────────────────────────────────────────────────────

const KIND_KEYS: Record<string, MessageKey> = {
  ball_machine: 'booking.assets.kind.ball_machine',
  racket: 'booking.assets.kind.racket',
  camera: 'booking.assets.kind.camera',
  other: 'booking.assets.kind.other',
};

/** The kind a piece of equipment is, in words — the same keys the registry uses. */
export function assetKindLabel(t: (k: MessageKey) => string, kind: unknown): string {
  return t(KIND_KEYS[String(kind ?? '')] ?? KIND_KEYS.other);
}

/**
 * The refusals the member equipment command can answer with, and the sentence
 * each becomes HERE — where no court is involved, so the court sheet's
 * «banen ble ikke booket» would be wrong. CODES on the wire, words here.
 */
const ALONE_REFUSAL: Record<string, MessageKey> = {
  asset_taken: 'mobile.book.equipment.alone.error.taken',
  dead_gap: 'mobile.book.equipment.alone.error.taken',
  asset_inactive: 'mobile.book.equipment.alone.error.unavailable',
  asset_not_found: 'mobile.book.equipment.alone.error.unavailable',
  outside_opening_hours: 'mobile.book.equipment.alone.error.unavailable',
  off_grid: 'mobile.book.equipment.alone.error.unavailable',
  booking_hours_not_set: 'mobile.book.notOpen',
  asset_unpriceable: 'mobile.book.equipment.alone.error.generic',
  asset_rejected: 'mobile.book.equipment.alone.error.generic',
};

/** What one piece costs for a range, in whole kroner, from its own zones. */
export function equipmentPriceFor(
  asset: any,
  date: string,
  startTime: string,
  endTime: string,
  cfg: any,
): number {
  return calcAssetBookingPrice(asset, date, startTime, endTime, cfg || {}).total || 0;
}

/** The assets a member may book at all: offered, and kept here. */
export function bookableAssets(assets: any[] = []): any[] {
  return (assets || []).filter((a) => a && a.active !== false);
}

const hoursOf = (cfg: any) =>
  typeof cfg?.openTime === 'string' && typeof cfg?.closeTime === 'string'
    ? { openTime: cfg.openTime as string, closeTime: cfg.closeTime as string }
    : {};

export interface EquipmentAloneProps {
  member: any;
  assets: any[];
  assetBookings: any[];
  /** The STORED booking configuration — what the server reads for equipment. */
  cfg: any;
  /** The day picked on the shared strip, and whether it is today. */
  date: string;
  isToday: boolean;
  /** The lengths the club offers, and the one chosen on the shared chips ('any' = shortest). */
  durations: number[];
  duration: number | 'any';
  /** The member equipment command on the player surface (`useAssets().addMemberAssetBooking`). */
  addAssetBooking?: (intent: any) => Promise<any>;
  onBooked?: (assetBooking: any) => void;
}

interface EquipmentPick {
  assetId: string;
  startTime: string;
  endTime: string;
  durationMin: number;
}

/**
 * Every start time on the day with a free piece for the chosen length, then
 * the pieces free at that time, then a sheet with the price and the booking
 * button — the court flow, with the machine in the court's place.
 */
export function EquipmentAlone({
  member,
  assets,
  assetBookings,
  cfg,
  date,
  isToday,
  durations,
  duration,
  addAssetBooking,
  onBooked,
}: EquipmentAloneProps) {
  const t = useT();
  const offered = bookableAssets(assets);
  const baseDur = duration === 'any' ? Math.min(...durations) : duration;
  const [selTime, setSelTime] = useState<string | null>(null);
  const [pick, setPick] = useState<EquipmentPick | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<any>(null);
  const [payNowFor, setPayNowFor] = useState<string | null>(null);

  const nowHHMM = (() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
  })();
  const slots = findFreeAssetSlots(offered, assetBookings || [], date, baseDur, {
    ...hoursOf(cfg),
    ...(typeof cfg?.stepMin === 'number' ? { stepMin: cfg.stepMin } : {}),
  }).filter((s) => !isToday || s.startTime > nowHHMM);
  const selSlot = selTime ? (slots.find((s) => s.startTime === selTime) ?? null) : null;

  /** The lengths actually bookable on this piece from the chosen start. */
  const durationsFor = (assetId: string, startTime: string): number[] =>
    durations.filter((d) =>
      availableAssetsFor(
        offered,
        assetBookings || [],
        { date, startTime, endTime: assetBookingEndTime(startTime, d) },
        hoursOf(cfg),
      ).some((a) => a.id === assetId),
    );

  const choose = (assetId: string, d: number) => {
    if (!selSlot) return;
    setError(null);
    setPick({
      assetId,
      startTime: selSlot.startTime,
      endTime: assetBookingEndTime(selSlot.startTime, d),
      durationMin: d,
    });
  };

  const confirm = async () => {
    if (!pick || !addAssetBooking) return;
    setBusy(true);
    setError(null);
    // THE INTENT, and nothing about its price: the server prices the machine
    // from its own zones, as it prices a court.
    const res = await addAssetBooking({
      assetId: pick.assetId,
      date,
      startTime: pick.startTime,
      durationMin: pick.durationMin,
    });
    setBusy(false);
    if (res?.success && res.value) {
      setDone(res.value);
      setPick(null);
      setSelTime(null);
      onBooked?.(res.value);
      // `pending` is the SERVER's word for «priced and not paid» — the sheet
      // opens for that and for nothing else.
      if (paymentDueFor(res.value)) setPayNowFor(res.value.id);
      return;
    }
    const key = typeof res?.code === 'string' ? ALONE_REFUSAL[res.code] : undefined;
    setError(key ? t(key) : res?.error || t('mobile.book.failed'));
  };

  const asset = pick ? offered.find((a) => a.id === pick.assetId) : null;
  const price =
    pick && asset ? equipmentPriceFor(asset, date, pick.startTime, pick.endTime, cfg) : 0;
  const doneAsset = done ? offered.find((a) => a.id === done.assetId) : null;
  const doneName = doneAsset?.name ?? done?.assetId ?? '';

  return (
    <>
      {/* Start times with a free piece */}
      <div className="@wide:col-start-1 @wide:row-start-4">
        <SectionTitle>
          {t('mobile.book.equipment.alone.freeTimes', { day: relativeDayLabel(date) })}
        </SectionTitle>
        {slots.length === 0 ? (
          <EmptyState
            title={t('mobile.book.equipment.alone.none')}
            hint={t('mobile.book.equipment.alone.noneHint')}
          />
        ) : (
          <div className="grid grid-cols-4 gap-1.5">
            {slots.map((s) => {
              const active = s.startTime === selTime;
              return (
                <button
                  key={s.startTime}
                  type="button"
                  onClick={() => {
                    setPick(null);
                    setSelTime(active ? null : s.startTime);
                  }}
                  aria-pressed={active}
                  className="rounded-xl py-2 text-center active:scale-[0.97] transition-transform"
                  style={{
                    background: active ? '#0f1611' : '#fff',
                    color: active ? '#fff' : '#0f1611',
                    border: `1px solid ${active ? '#0f1611' : '#e7eae3'}`,
                  }}
                >
                  <div className="text-[14px] font-semibold tabular-nums">{s.startTime}</div>
                </button>
              );
            })}
          </div>
        )}
        <div className="text-[10px] text-ink-3 px-1 mt-1.5">
          {selSlot
            ? t('mobile.book.equipment.alone.pick', {
                from: selSlot.startTime,
                to: selSlot.endTime,
              })
            : t('mobile.book.pickTimeFirst')}
        </div>
      </div>

      {/* The pieces free at that time */}
      <div className="@wide:col-start-2 @wide:row-start-2 @wide:row-span-4">
        <SectionTitle>
          {selSlot
            ? t('mobile.book.equipment.alone.freeAt', {
                from: selSlot.startTime,
                to: selSlot.endTime,
              })
            : t('mobile.book.equipment.alone.modeEquipment')}
        </SectionTitle>
        <div className="space-y-1.5">
          {offered.map((a) => {
            const freeNow = selSlot ? selSlot.freeAssetIds.includes(a.id) : null;
            if (freeNow === false) return null;
            const chosen = pick?.assetId === a.id;
            const opts = selSlot ? durationsFor(a.id, selSlot.startTime) : [];
            return (
              <Card
                key={a.id}
                className="!py-2.5"
                onClick={
                  freeNow && opts.length === 1 && !chosen ? () => choose(a.id, opts[0]) : undefined
                }
                accent={chosen ? '#0f1611' : freeNow ? '#c2f02f' : undefined}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{
                      width: 36,
                      height: 36,
                      background: chosen ? '#0f1611' : '#eef0ea',
                      color: chosen ? '#fff' : '#0f1611',
                    }}
                  >
                    {chosen ? <Check size={16} strokeWidth={2.5} /> : <Package size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-ink">{a.name}</div>
                    <div className="text-[11px] text-ink-3">{assetKindLabel(t, a.kind)}</div>
                  </div>
                  <div
                    className="font-mono text-[10px] flex-shrink-0 font-bold"
                    style={{ color: freeNow ? '#3f6212' : 'var(--ink-3)' }}
                  >
                    {selSlot
                      ? chosen
                        ? t('mobile.book.chosen')
                        : opts.length === 1
                          ? t('mobile.book.freeBookIt')
                          : t('mobile.book.pickLength')
                      : a.defaultPricePerHour > 0
                        ? t('booking.assets.pricePerHour', { n: a.defaultPricePerHour })
                        : t('booking.assets.free')}
                  </div>
                </div>
                {selSlot && freeNow && opts.length > 1 && (
                  <div className="flex gap-1.5 mt-2" role="presentation">
                    {opts.map((d) => {
                      const end = assetBookingEndTime(selSlot.startTime, d);
                      const p = equipmentPriceFor(a, date, selSlot.startTime, end, cfg);
                      const on = pick?.assetId === a.id && pick.durationMin === d;
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            choose(a.id, d);
                          }}
                          aria-label={t('mobile.book.minutesUntil', { minutes: d, end })}
                          className="rounded-lg px-2.5 py-1 text-[11px] font-semibold"
                          style={{
                            background: on ? '#0f1611' : '#c2f02f',
                            color: on ? '#fff' : '#0f1611',
                          }}
                        >
                          {d} min · {p > 0 ? `${p} kr` : t('mobile.book.freeLower')}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* The sheet: piece · time · price · book — or the confirmation. */}
      {(pick || done) && (
        <div
          className="sticky bottom-0 -mx-4 px-3 pt-2 @wide:col-start-2 @wide:row-start-6 @wide:mx-0 @wide:px-0"
          style={{ zIndex: 10 }}
        >
          <div
            className="rounded-3xl px-4 pt-4 pb-4"
            style={{
              background: '#0f1611',
              color: '#fff',
              boxShadow: '0 -8px 30px rgba(15,22,17,0.25)',
            }}
            // A REGION, NOT A DIALOG (#900), as the court sheet is.
            role="region"
            aria-label={
              done
                ? t('mobile.book.equipment.alone.confirmedAria')
                : t('mobile.book.equipment.alone.sheetAria')
            }
          >
            {done ? (
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ width: 40, height: 40, background: '#c2f02f', color: '#0f1611' }}
                >
                  <Check size={20} strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold" style={{ fontSize: 18 }}>
                    {/* NOT «utstyret er ditt» while it still owes money — the same
                        rule as the court's confirmation. */}
                    {paymentDueFor(done)
                      ? t('mobile.book.equipment.alone.heldUnpaid')
                      : t('mobile.book.equipment.alone.yours')}
                  </div>
                  <div className="text-[12px] opacity-80 flex items-center gap-1">
                    <Package size={12} /> {doneName} · {relativeDayLabel(done.date)}{' '}
                    {done.startTime}–{done.endTime}
                  </div>
                  <div className="mt-2">
                    {/* THE EQUIPMENT PAYMENT PATH, not the court's: the machine
                        has its own hold and is addressed by its own id. */}
                    <PayForBooking
                      booking={done}
                      subject="equipment"
                      summary={`${doneName} · ${relativeDayLabel(done.date)} ${done.startTime}–${done.endTime}`}
                      autoOpen={payNowFor === done.id}
                      onPaid={() => setDone({ ...done, paymentStatus: 'paid' })}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDone(null)}
                  aria-label={t('mobile.common.close')}
                  className="p-1 opacity-70"
                >
                  <X size={18} />
                </button>
              </div>
            ) : pick ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-wider opacity-60">
                      {relativeDayLabel(date)} · {pick.durationMin} min
                    </div>
                    <div
                      className="font-display font-semibold leading-tight"
                      style={{ fontSize: 20 }}
                    >
                      {pick.startTime}–{pick.endTime}
                    </div>
                    <div className="text-[12px] opacity-80 flex items-center gap-1 mt-0.5">
                      <Package size={12} />{' '}
                      {t('mobile.book.equipment.alone.bookedFor', {
                        asset: asset?.name ?? pick.assetId,
                        name: member?.name || t('mobile.book.you'),
                      })}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display font-semibold" style={{ fontSize: 22 }}>
                      {price > 0 ? `${price} kr` : t('mobile.activities.free')}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPick(null)}
                      className="text-[11px] opacity-60 underline"
                    >
                      {t('mobile.common.cancel')}
                    </button>
                  </div>
                </div>
                {error && (
                  <div
                    className="rounded-xl px-3 py-2 text-[12px] mt-2"
                    style={{ background: '#ffe4e6', color: '#be123c' }}
                  >
                    {error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={busy || !addAssetBooking}
                  className="w-full rounded-2xl font-semibold text-[14px] py-3 mt-3 active:scale-[0.98] transition-transform disabled:opacity-40"
                  style={{ background: '#c2f02f', color: '#0f1611' }}
                >
                  {busy
                    ? t('mobile.book.booking')
                    : price > 0
                      ? t('mobile.book.payAndBook', { price: String(price) })
                      : t('mobile.book.equipment.alone.book')}
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
